"use client";

import { type ReactNode, useEffect, useRef } from "react";
import { useAnalytics } from "@/lib/analytics/hooks";
import { slotTimingProps } from "@/lib/analytics/slot-timing";
import { trackMonetizationEvent } from "@/lib/analytics/track";
import { recordArmImpression } from "@/lib/monetization/ad-context-client";
import { readVariantId } from "@/lib/monetization/variant-id";
import { genClickId, MonetizedUnitProvider } from "./monetized-unit-context";

type Props = {
	platform: string;
	surface: string;
	locale?: string;
	jobId?: string | null;
	/**
	 * Extra PostHog-only props merged into the impression event — auction
	 * decision telemetry (`market`, `decision_reason`, `eligible_arms`). Not
	 * persisted to D1 (the beacon schema stays fixed).
	 */
	extra?: Record<string, unknown>;
	/** Applied to the observed wrapper div so the card keeps its own styling. */
	className?: string;
	/** Stable handle for the perf-invariants e2e (tests/e2e/perf-invariants.spec.ts):
	 * the rendered offer must be selectable by the same testid as its skeleton
	 * and empty states, or "the box never collapses" cannot be asserted. */
	testId?: string;
	/**
	 * Fired from the SAME single-fire 50%-visibility observer that sends
	 * `affiliate_impression`, so a caller's own bookkeeping cannot drift from
	 * the impression it is supposed to be counting. This is how the session
	 * frequency budget is charged (`useAdGuardrails().recordImpression`): a
	 * unit that resolved an offer but was never on screen must not spend a
	 * slot — the lazy offer fetch starts a full viewport early, so "resolved"
	 * and "seen" are genuinely different populations.
	 */
	onImpression?: () => void;
	/**
	 * Optional gate checked at fire time. Return false when this unit's ad
	 * request already reached a terminal outcome (an impression already fired
	 * for it, or `pagehide` emitted its terminal): the observer then stands down
	 * without emitting, so a remount or a late visibility hit cannot add a
	 * second terminal to one `affiliate_slot_requested`
	 * (`useSlotRequestTerminal().canImpress`).
	 */
	canFire?: () => boolean;
	children: ReactNode;
};

const VISIBILITY_THRESHOLD = 0.5;

/**
 * Wraps an affiliate card and fires a single `affiliate_impression` (PostHog +
 * D1, via the same beacon path as `affiliate_click`) the first time the card is
 * at least 50% visible. Pairs with `affiliate_click` so CTR = clicks /
 * impressions per `surface` — the standing "always measure engagement" signal.
 *
 * Renders one wrapper `div` (the observed element); pass `className` to move the
 * card's styling onto it rather than nesting an extra box.
 */
export function AffiliateImpression({
	platform,
	surface,
	locale,
	jobId = null,
	extra,
	className,
	testId,
	onImpression,
	canFire,
	children,
}: Props) {
	const { trackAffiliateImpression } = useAnalytics();
	const ref = useRef<HTMLDivElement>(null);
	const firedRef = useRef(false);
	// Mint once per mounted unit. The same id is provided to the inner
	// `AffiliateLink` so impression + click share a join key for the postback.
	const clickIdRef = useRef<string | null>(null);
	if (!clickIdRef.current) clickIdRef.current = genClickId();
	const clickId = clickIdRef.current;
	// Ref, not a dep: `extra` is an object literal at most call sites, so a
	// render-fresh identity must not re-arm the (single-fire) observer.
	const extraRef = useRef(extra);
	extraRef.current = extra;
	// Same reasoning as `extraRef`: most callers pass an inline arrow, and the
	// observer must stay single-armed.
	const onImpressionRef = useRef(onImpression);
	onImpressionRef.current = onImpression;
	const canFireRef = useRef(canFire);
	canFireRef.current = canFire;

	useEffect(() => {
		const el = ref.current;
		if (!el || typeof IntersectionObserver === "undefined") return;

		const observer = new IntersectionObserver(
			(entries) => {
				const entry = entries[0];
				if (!entry?.isIntersecting || firedRef.current) return;
				firedRef.current = true;
				if (canFireRef.current && !canFireRef.current()) {
					observer.disconnect();
					return;
				}
				trackAffiliateImpression({
					platform,
					surface,
					locale,
					job_id: jobId,
					click_id: clickId,
					...extraRef.current,
					// Fire-time viewability (PR-5) — how much of the unit was actually
					// on screen when the 50%-threshold impression fired, straight from
					// this same observer entry. Placed after the `extra` spread so a
					// caller can never shadow the real measured value.
					viewport_ratio: entry.intersectionRatio,
					// When and where on the page the unit became viewable — the
					// numbers that make "it painted after I scrolled past" measurable.
					...slotTimingProps(entry),
				});
				trackMonetizationEvent({
					eventKind: "affiliate_impression",
					platform,
					surface,
					clickId,
					// Mirror the copy-variant id into D1, not just PostHog: the D1
					// beacon is the store this project can actually settle a copy
					// test from. Read off `extra` so the caller keeps one source.
					variantId: readVariantId(extraRef.current),
					locale,
				});
				// Wave 4c per-visitor fatigue ledger (localStorage, try/catch).
				recordArmImpression(platform);
				onImpressionRef.current?.();
				observer.disconnect();
			},
			{ threshold: VISIBILITY_THRESHOLD },
		);
		observer.observe(el);
		return () => observer.disconnect();
	}, [platform, surface, locale, jobId, clickId, trackAffiliateImpression]);

	return (
		<MonetizedUnitProvider clickId={clickId}>
			<div ref={ref} className={className} data-testid={testId}>
				{children}
			</div>
		</MonetizedUnitProvider>
	);
}
